import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AgenticMeta } from '../../models/agenticSearch.interface';
import type { AgenticAttempt } from '../../api/search/searchApi';
import {
  AgenticSearchState,
  AgenticSearchStatus,
  initialState,
} from '../types/agenticSearchTypes';
import { clearSearch, clearVideos } from './searchVideosSlice';

// Agentic search envelope state: the meta the grid and status component read
// (confidence, reasoning, rewrite caption, timing) while the mapped rows ride
// the existing search slice unchanged. Written only by the agentic hook.

const VALID_STATUSES: readonly AgenticSearchStatus[] = ['idle', 'thinking', 'done', 'error'];

// Rebuild a persisted agentic substate defensively. localStorage content is
// untrusted input: a blob written by any prior build (or edited by hand) may
// be sitting there. A wrong overall shape or an unknown status falls back to
// a COPY of initialState, per-field wrong types are dropped, unknown keys are
// ignored, and a persisted 'thinking' lands as 'idle' because an in-flight
// search cannot survive a reload; rendering a spinner with no request behind
// it would be a lie. Every return here is a fresh object on purpose; see the
// REHYDRATE comment below for why the reference matters.
const normalizeRehydrated = (candidate: unknown): AgenticSearchState => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return { ...initialState };
  }
  const raw = candidate as Record<string, unknown>;
  if (
    typeof raw.status !== 'string' ||
    !VALID_STATUSES.includes(raw.status as AgenticSearchStatus)
  ) {
    return { ...initialState };
  }
  const next: AgenticSearchState = {
    status: raw.status === 'thinking' ? 'idle' : (raw.status as AgenticSearchStatus),
  };
  if (typeof raw.confident === 'boolean') next.confident = raw.confident;
  if (typeof raw.reasoning === 'string') next.reasoning = raw.reasoning;
  if (Array.isArray(raw.attempts)) next.attempts = raw.attempts as AgenticAttempt[];
  if (typeof raw.queryUsed === 'string') next.queryUsed = raw.queryUsed;
  if (typeof raw.originalQuery === 'string') next.originalQuery = raw.originalQuery;
  if (typeof raw.fetchedAt === 'number') next.fetchedAt = raw.fetchedAt;
  if (typeof raw.errorKind === 'string') next.errorKind = raw.errorKind;
  return next;
};

const agenticSearchSlice = createSlice({
  name: 'agentic',
  initialState,
  reducers: {
    // A new search wipes everything from the previous one; only the query
    // the user typed carries forward into the thinking state.
    agenticSearchStarted(_state, action: PayloadAction<{ query: string }>) {
      return {
        ...initialState,
        status: 'thinking' as const,
        originalQuery: action.payload.query,
      };
    },
    // Spread only the DEFINED meta fields so absent wire keys stay absent
    // state keys, mirroring the contract's absent-not-null semantics.
    agenticSearchSucceeded(state, action: PayloadAction<{ meta: AgenticMeta; fetchedAt: number }>) {
      const { meta, fetchedAt } = action.payload;
      const next: AgenticSearchState = {
        status: 'done',
        confident: meta.confident,
        fetchedAt,
      };
      if (state.originalQuery !== undefined) next.originalQuery = state.originalQuery;
      if (meta.reasoning !== undefined) next.reasoning = meta.reasoning;
      if (meta.attempts !== undefined) next.attempts = meta.attempts;
      if (meta.queryUsed !== undefined) next.queryUsed = meta.queryUsed;
      return next;
    },
    agenticSearchFailed(state, action: PayloadAction<{ errorKind: string }>) {
      const next: AgenticSearchState = {
        status: 'error',
        errorKind: action.payload.errorKind,
      };
      if (state.originalQuery !== undefined) next.originalQuery = state.originalQuery;
      return next;
    },
    agenticReset() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Every existing clear path resets agentic state with ZERO edits in the
      // shared files: the legacy slice keeps dispatching the same actions it
      // always has and this slice listens in.
      .addCase(clearSearch, () => initialState)
      .addCase(clearVideos, () => initialState)
      // redux-persist rehydration. The store's persistConfig sets no
      // stateReconciler, so persistReducer defaults to autoMergeLevel1
      // (node_modules/redux-persist/lib/persistReducer.js line 44). Verified
      // from node_modules/redux-persist/lib/stateReconciler/autoMergeLevel1.js
      // lines 25 to 37: after the root reducer has processed the REHYDRATE
      // action, the reconciler walks the inbound persisted keys and hard-sets
      // each one into the result UNLESS originalState[key] !==
      // reducedState[key], i.e. unless this reducer CHANGED its substate (a
      // reference comparison) while handling REHYDRATE; a changed key keeps
      // the reducer's value and the raw inbound value is skipped. That skip
      // rule is exactly why this normalization sticks. It is also why the
      // handler always returns a FRESH object when the persisted key exists:
      // returning the shared initialState reference would compare as
      // unchanged and let the raw persisted blob straight through the merge.
      // (Every slice is saved, this one included: store/index.ts. The
      // normalization is load bearing.)
      .addCase(
        'persist/REHYDRATE',
        (state, action: { type: 'persist/REHYDRATE'; payload?: unknown }) => {
          const payload = action.payload;
          if (!payload || typeof payload !== 'object' || !('agentic' in payload)) {
            // Nothing persisted for this key (a blob from before the key
            // existed, or a fresh browser): keep state. The inbound blob has
            // no 'agentic' key for autoMergeLevel1 to copy, so the boot
            // initial state stays in place.
            return state;
          }
          return normalizeRehydrated((payload as { agentic?: unknown }).agentic);
        }
      );
  },
});

export const { agenticSearchStarted, agenticSearchSucceeded, agenticSearchFailed, agenticReset } =
  agenticSearchSlice.actions;
export default agenticSearchSlice.reducer;
